// WP-1.18 (SR-3, S-10): attachment queries/mutations. The content endpoint is auth-gated and
// same-origin, so an <img src> / <a href> carries the session cookie automatically — no need
// to fetch bytes in JS just to display them.
import {
  attachmentListResponseSchema,
  attachmentMetaSchema,
  type AttachmentMeta,
} from '@pfos/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from './api';

export const ATTACHMENTS_KEY = ['attachments'] as const;

export function attachmentContentUrl(id: string): string {
  return `/api/v1/attachments/${id}/content`;
}

export function useAttachments(
  entityType: string,
  entityId: string,
): UseQueryResult<AttachmentMeta[]> {
  return useQuery({
    queryKey: [...ATTACHMENTS_KEY, entityType, entityId],
    queryFn: async () => {
      const params = new URLSearchParams({ entityType, entityId });
      const body = attachmentListResponseSchema.parse(
        await api.get(`/api/v1/attachments?${params.toString()}`),
      );
      return body.attachments;
    },
  });
}

export interface UploadInput {
  entityType: string;
  entityId: string;
  filename: string;
  contentType: string;
  dataBase64: string;
}

export function useUploadAttachment(): UseMutationResult<AttachmentMeta, Error, UploadInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input) =>
      attachmentMetaSchema.parse(await api.post('/api/v1/attachments', input)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ATTACHMENTS_KEY });
    },
  });
}

export function useDeleteAttachment(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/attachments/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ATTACHMENTS_KEY });
    },
  });
}
